/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * VPS Context Assembly Service
 * 
 * API client for VPS Context Assembly endpoints.
 * Uses shared response types and handleApiResponse for consistent error handling.
 */

import { authenticatedFetch } from '@shared/lib/api';
import { handleApiResponse } from '@shared/lib/apiResponse';
import type {
  AssembledContext,
  AssemblyStrategy,
  ContextAssemblyConfig,
} from '@shared/schema/context';
import type {
  AssembleContextResponseData,
  ContextStrategiesResponseData,
  ContextConfigResponseData,
  UpdateContextConfigResponseData,
} from '@shared/types/responses/context';

// Re-export types for convenience
export type { AssemblyStrategy, AssembledContext, ContextAssemblyConfig as ContextConfig };

export interface StrategyDescription {
  id: AssemblyStrategy;
  name: string;
  description: string;
  recommended?: boolean;
}

/**
 * Assemble context for a task
 */
export async function assembleContext(
  taskDescription: string,
  options?: {
    strategy?: AssemblyStrategy;
    maxPlaybooks?: number;
    maxSkills?: number;
    productId?: string;
    includeConnectorSkills?: boolean;
    minRelevanceScore?: number;
  }
): Promise<AssembledContext> {
  const response = await authenticatedFetch('/api/context/assemble', {
    method: 'POST',
    body: {
      taskDescription,
      strategy: options?.strategy,
      maxPlaybooks: options?.maxPlaybooks,
      maxSkills: options?.maxSkills,
      productId: options?.productId,
      includeConnectorSkills: options?.includeConnectorSkills,
      minRelevanceScore: options?.minRelevanceScore,
    },
  });
  const data = await handleApiResponse<AssembleContextResponseData>(response);
  return data.context;
}

/**
 * Get available assembly strategies
 */
export async function getAssemblyStrategies(): Promise<{
  strategies: StrategyDescription[];
  defaultStrategy: AssemblyStrategy;
}> {
  const response = await authenticatedFetch('/api/context/strategies');
  const data = await handleApiResponse<ContextStrategiesResponseData>(response);
  
  // Transform to StrategyDescription format
  const strategies: StrategyDescription[] = data.strategies.map(id => ({
    id,
    name: id.charAt(0).toUpperCase() + id.slice(1),
    description: data.descriptions[id] || '',
    recommended: id === data.defaultStrategy,
  }));
  
  return {
    strategies,
    defaultStrategy: data.defaultStrategy,
  };
}

/**
 * Get context assembly configuration
 */
export async function getContextConfig(): Promise<ContextAssemblyConfig> {
  const response = await authenticatedFetch('/api/context/config');
  const data = await handleApiResponse<ContextConfigResponseData>(response);
  return data.config;
}

/**
 * Update context assembly configuration
 */
export async function updateContextConfig(
  config: Partial<ContextAssemblyConfig>
): Promise<ContextAssemblyConfig> {
  const response = await authenticatedFetch('/api/context/config', {
    method: 'PUT',
    body: config,
  });
  const data = await handleApiResponse<UpdateContextConfigResponseData>(response);
  return data.config;
}
